import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { bookingService } from '../services/bookingService';
import { MeetingContext, isClientContext } from '../types/booking';
import { useMeetingPhase } from '../hooks/useMeetingPhase';
import WaitingRoom from '../components/branch/WaitingRoom';
import MultiDeviceModal from '../components/branch/MultiDeviceModal';
import MeetingSession from '../components/branch/MeetingSession';
import { supportsWebGL, isLowCapabilityDevice } from '../utils/webglSupport';

const BranchScene = lazy(() => import('../components/branch/BranchScene'));
const BranchWalkthrough2D = lazy(() => import('../components/branch/BranchWalkthrough2D'));

type MeetingError = 'expired' | 'completed' | 'invalid' | null;

// ---------------------------------------------------------------------------
// Device fingerprint (AC:7) — non-PII hash of browser characteristics
// ---------------------------------------------------------------------------

function generateFingerprint(): string {
  const raw = `${navigator.userAgent}|${screen.width}|${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

// ---------------------------------------------------------------------------
// Error / terminal views
// ---------------------------------------------------------------------------

const ExpiredView: React.FC = () => (
  <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-6">
      <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h1 className="text-white text-2xl font-semibold mb-2">
      This appointment has passed
    </h1>
    <p className="text-slate-400 text-sm mb-8 max-w-sm">
      The meeting window has closed. Please book a new appointment to speak with your advisor.
    </p>
    <Link
      to="/trading/appointments/book"
      className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-semibold transition-colors"
    >
      Book New Appointment
    </Link>
  </div>
);

const CompletedView: React.FC = () => (
  <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
    <div className="w-16 h-16 rounded-full bg-teal-900/40 flex items-center justify-center mb-6">
      <svg className="w-8 h-8 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h1 className="text-white text-2xl font-semibold mb-2">Meeting Complete</h1>
    <p className="text-slate-400 text-sm mb-8 max-w-sm">
      Your meeting has ended. View your meeting summary and minutes in your appointment history.
    </p>
    <Link
      to="/trading/appointments"
      className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-semibold transition-colors"
    >
      View Meeting History
    </Link>
  </div>
);

const InvalidView: React.FC = () => (
  <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
    <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mb-6">
      <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h1 className="text-white text-2xl font-semibold mb-2">Invalid Meeting Link</h1>
    <p className="text-slate-400 text-sm mb-8 max-w-sm">
      This meeting link is not valid or has been revoked. Check your email for the correct link.
    </p>
    <Link
      to="/trading/appointments"
      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
    >
      Go to Appointments
    </Link>
  </div>
);

const LoadingView: React.FC = () => (
  <div className="fixed inset-0 bg-slate-950 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
  </div>
);

// Placeholder shown for access-card phase until v8-4-1+ implements the 3D scene
const AccessCardPlaceholder: React.FC = () => (
  <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-6 text-center">
    <div className="w-full max-w-sm bg-slate-900 border border-slate-700/60 rounded-2xl p-8">
      <div className="w-16 h-16 rounded-xl bg-teal-900/40 flex items-center justify-center mb-6 mx-auto">
        <svg className="w-8 h-8 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
        </svg>
      </div>
      <h2 className="text-white text-lg font-semibold mb-2">Access Granted</h2>
      <p className="text-slate-400 text-sm">
        Preparing your virtual branch experience...
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main BranchJoin component
// ---------------------------------------------------------------------------

const BranchJoin: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isGuest = searchParams.get('guest') === '1';

  const { phase, advancePhase } = useMeetingPhase('validate');
  const [context, setContext] = useState<MeetingContext | null>(null);
  const [meetingError, setMeetingError] = useState<MeetingError>(null);
  const [multiDeviceConflict, setMultiDeviceConflict] = useState(false);

  // AC:7 — generate device fingerprint once on mount
  const fingerprint = useMemo(() => generateFingerprint(), []);

  // Step 1 — resolve the meeting link token
  useEffect(() => {
    if (!token) {
      setMeetingError('invalid');
      return;
    }

    bookingService.joinMeetingLink(token, isGuest)
      .then(ctx => {
        setContext(ctx);
        const s = ctx.state;
        if (s === 'waiting') {
          advancePhase('waiting-room');
        } else if (s === 'ready' || s === 'active') {
          advancePhase('access-card');
        } else if (s === 'completed') {
          setMeetingError('completed');
        } else if (s === 'expired') {
          setMeetingError('expired');
        }
      })
      .catch(() => {
        setMeetingError('invalid');
      });
  }, [token, isGuest, advancePhase]);

  // Step 2 — when entering access-card phase, start the meeting session (AC:1–2)
  useEffect(() => {
    if (phase !== 'access-card' || isGuest || !context || !isClientContext(context)) return;

    bookingService.startMeetingSession(context.booking_id, fingerprint)
      .then(result => {
        if (result.status === 'conflict') {
          setMultiDeviceConflict(true);
        } else {
          // No conflict — advance to walkthrough immediately (AC:1)
          advancePhase('walkthrough');
        }
      })
      .catch((err) => {
        console.warn('Meeting session start failed (non-critical):', err);
        // Still advance to walkthrough even if session tracking fails
        advancePhase('walkthrough');
      });
  }, [phase, context, isGuest, fingerprint, advancePhase]);

  // Transfer session to this device when user clicks "Continue Here" (AC:3)
  const handleTransfer = () => {
    if (!context || !isClientContext(context)) return;
    bookingService.transferMeetingSession(context.booking_id, fingerprint)
      .then(() => {
        setMultiDeviceConflict(false);
        advancePhase('walkthrough');
      })
      .catch((err) => {
        // Keep modal open on error so the user can retry
        console.warn('Session transfer failed:', err);
      });
  };

  // Terminal error states
  if (meetingError === 'expired') return <ExpiredView />;
  if (meetingError === 'completed') return <CompletedView />;
  if (meetingError === 'invalid') return <InvalidView />;

  // Phase-based rendering
  if (phase === 'validate') return <LoadingView />;

  if (phase === 'waiting-room') {
    return (
      <WaitingRoom
        context={context}
        onReady={() => advancePhase('access-card')}
      />
    );
  }

  if (phase === 'access-card' || phase === 'access-granted') {
    return (
      <>
        <AccessCardPlaceholder />
        {multiDeviceConflict && (
          <MultiDeviceModal
            onContinueHere={handleTransfer}
            onCancel={() => {
              setMultiDeviceConflict(false);
              advancePhase('waiting-room');
            }}
          />
        )}
      </>
    );
  }

  // AC:1, 8 — walkthrough phase: 3D if WebGL available and not low-capability, else 2D fallback
  if (phase === 'walkthrough' && context && isClientContext(context)) {
    const parts = (context.client_name ?? '').trim().split(' ');
    const surname = parts.length > 1 ? parts[parts.length - 1] : parts[0] ?? 'Guest';
    const use3D = supportsWebGL() && !isLowCapabilityDevice();
    if (use3D) {
      return (
        <Suspense fallback={<LoadingView />}>
          <BranchScene
            roomName={context.room_name}
            clientSurname={surname}
            guestName={context.guest_name}
            onWalkthroughComplete={() => advancePhase('meeting')}
          />
        </Suspense>
      );
    }
    // AC:2–4 — 2D fallback for mobile / no-WebGL
    // AC:3 — TTS greeting identical to 3D path
    const speakGreeting = () => {
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
      const text = context.guest_name
        ? `Please follow me, Mr ${surname} — and welcome, ${context.guest_name}. Our meeting room is ${context.room_name}.`
        : `Please follow me, Mr ${surname}. Our meeting room is ${context.room_name}.`;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    };
    return (
      <Suspense fallback={<LoadingView />}>
        <BranchWalkthrough2D
          onComplete={() => advancePhase('meeting')}
          onMount={speakGreeting}
        />
      </Suspense>
    );
  }

  // 'meeting' phase — FAIS overlay + banker chat (SSE in v8-6-2)
  if (phase === 'meeting' && context) {
    return <MeetingSession context={context} onEnded={() => advancePhase('ended')} />;
  }

  // AC:9-10 — 'ended' phase — meeting summary screen (v8-6-3)
  if (phase === 'ended') {
    return (
      <div className="fixed inset-0 bg-gray-950 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-6">
          <div className="text-green-400 text-5xl">✓</div>
          <h1 className="text-white text-2xl font-bold">Your meeting has ended</h1>
          <p className="text-gray-400">
            A summary will be sent to your email. You can also view your meeting history
            in the appointments section.
          </p>
          <Link
            to="/trading/appointments"
            className="inline-block mt-4 px-6 py-3 bg-amber-500 text-black rounded font-bold"
          >
            View Meeting History
          </Link>
        </div>
      </div>
    );
  }

  return <LoadingView />;
};

export default BranchJoin;
